<template>
    <div>
        <div class="flex items-center gap-2 mb-3">
            <h3 class="text-base font-semibold text-gray-900 dark:text-white">Team Members</h3>
            <span class="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-2 rounded-full bg-gray-100 dark:bg-gray-700 text-xs font-semibold text-gray-600 dark:text-gray-300">
                {{ props.members.length }}
            </span>
        </div>

        <div v-if="props.members.length > 0" class="relative mb-3">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607z" />
            </svg>
            <input
                v-model="memberFilter"
                type="text"
                placeholder="Filter members"
                class="w-full pl-9 pr-8 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-500"
            />
            <button
                v-if="memberFilter"
                type="button"
                @click="memberFilter = ''"
                class="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                title="Clear"
            >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-4 h-4">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>
        </div>

        <div v-if="filteredMembers.length > 0" class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl divide-y divide-gray-100 dark:divide-gray-700/60 overflow-hidden">
            <div v-for="member in filteredMembers" :key="member.id"
                 class="flex items-center gap-3 px-4 py-3">
                <UserAvatar :user="member" :size="40" />
                <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2 min-w-0">
                        <span class="block text-sm font-semibold text-gray-900 dark:text-white truncate">{{ displayName(member) }}</span>
                        <span v-if="isOwner(member)" class="flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300">
                            Owner
                        </span>
                    </div>
                    <span v-if="hasRealName(member)" class="block text-xs text-gray-500 dark:text-gray-400 truncate">({{ member.email }})</span>
                </div>
                <button
                    v-if="canRemove(member)"
                    type="button"
                    @click="handleRemoveMember(member)"
                    title="Remove from team"
                    class="flex-shrink-0 p-2 rounded-lg text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                    </svg>
                </button>
            </div>
        </div>
        <div v-else class="space-y-2 block mb-2 text-sm font-medium text-gray-900 dark:text-white">
            {{ props.members.length === 0 ? 'No members added' : 'No members match the filter' }}
        </div>
    </div>
</template>

<script setup>
import { defineProps, defineEmits, computed, ref } from 'vue';
import UserAvatar from '@/components/common/UserAvatar.vue';
import userStore from '@/store/user.store';

const props = defineProps({
    members: {
        type: Array,
        required: true
    },
    teamId: {
        type: String,
        default: null
    },
    ownerId: {
        type: String,
        default: null
    },
    canManage: {
        type: Boolean,
        default: false
    }
});

const memberFilter = ref('');

const filteredMembers = computed(() => {
    const q = memberFilter.value.trim().toLowerCase();
    if (!q) return props.members;
    return props.members.filter(m =>
        (m.name || '').toLowerCase().includes(q) ||
        (m.email || '').toLowerCase().includes(q)
    );
});

const currentUser = computed(() => userStore.state.userInfo);

// Sem nome definido, o email assume a linha principal (sem duplicar abaixo).
const displayName = (member) => {
    const name = (member?.name || '').trim();
    if (name) return name;
    return (member?.email || '').trim();
};

const hasRealName = (member) => {
    const name = (member?.name || '').trim();
    if (!name) return false;
    return name.toLowerCase() !== ((member?.email || '').trim().toLowerCase());
};

const isSelf = (member) => {    const me = currentUser.value;
    if (!me) return false;
    if (me.id && member.id && String(me.id) === String(member.id)) return true;
    if (me.email && member.email && String(me.email).toLowerCase() === String(member.email).toLowerCase()) return true;
    return false;
};

const isOwner = (member) => {
    if (!props.ownerId || !member?.id) return false;
    return String(member.id) === String(props.ownerId);
};

// Trash is shown only to those who can manage the team, never on your own row.
const canRemove = (member) => {
    if (isSelf(member)) return false;
    return props.canManage;
};

const emit = defineEmits(['member-removed']);

const handleRemoveMember = async (member) => {
    emit('member-removed', member);
};
</script>
